import "./MissingFieldsList.css"

/**
 * A single missing/invalid DTA field. Surfaced both by the editor's client-side pre-publish
 * validation and by the backend publish_validation (DRAFT -> REVIEW) failure response, which share
 * this shape.
 */
export interface ValidationError {
  field: string
  tab: string
  message: string
}

interface MissingFieldsListProps {
  errors: ValidationError[]
  /**
   * When provided, each field renders as a link that navigates the editor to the offending
   * tab/field. When omitted (e.g. backend errors surfaced in a page-level toast), fields render as
   * plain, non-interactive text.
   */
  onErrorClick?: (tab: string, field: string) => void
}

/**
 * Presentational bulleted list of missing/invalid DTA fields ({field, tab, message}). Rendered both
 * by the editor's ValidationErrorsAlert (with click-to-field links) and by the backend
 * publish-validation error path in WorkflowActions (plain text in a toast). Callers own the
 * surrounding container/Alert - this is just the list, so it never nests an Alert inside an Alert.
 */
const MissingFieldsList = ({ errors, onErrorClick }: MissingFieldsListProps) => (
  <ul className="missing-fields-list">
    {errors.map((e, i) => {
      // Guard the boundary: backend error JSON isn't guaranteed to carry `message`, and `field`
      // values aren't guaranteed unique — so fall back for the text and use a composite key.
      const text = e.message || e.field || "Missing field"
      return (
        <li key={`${e.field}-${i}`}>
          {onErrorClick ? (
            <a
              href="#"
              onClick={(ev) => {
                ev.preventDefault()
                onErrorClick(e.tab, e.field)
              }}
            >
              {text}
            </a>
          ) : (
            text
          )}
        </li>
      )
    })}
  </ul>
)

export default MissingFieldsList
